import { ApiCoreEntities } from './coreEntities';
import {
  Violation,
  MaintenanceRequest,
  Amenity,
  AmenityBooking,
  Inspection,
  PackageItem,
} from '../../types';

export class ApiViolationsMaintenance extends ApiCoreEntities {
  // ============ VIOLATIONS ============

  /** Normalize backend enum strings (uppercase) to lowercase for frontend type compatibility */
  private parseViolation(v: any): Violation {
    return {
      ...v,
      status:   (v.status   ?? 'open').toLowerCase() as Violation['status'],
      severity: (v.severity ?? 'medium').toLowerCase() as Violation['severity'],
      notes:    v.notes  ?? [],
      photos:   v.photos ?? [],
    };
  }

  async getViolations(condoId?: string): Promise<Violation[]> {
    // Sprint 32 — finding R9. The no-condoId path used to hit
    // GET /api/condo/violations, which 404s (no such mapping; the real
    // endpoint is /api/condo/violations/condo/{condoId}). Owner login
    // fires this on dashboard load before currentCondo resolves, so
    // every owner saw a 404 in the network panel. Short-circuit and
    // wait for the next call once currentCondo is set, same shape as
    // R8 above.
    if (!condoId) return [];
    const raw = await this.request<any[]>(`/condo/violations/condo/${condoId}`);
    return raw.map(v => this.parseViolation(v));
  }

  async getViolation(id: string): Promise<Violation> {
    const raw = await this.request<any>(`/condo/violations/${id}`);
    return this.parseViolation(raw);
  }

  async createViolation(violation: Omit<Violation, 'id'>): Promise<Violation> {
    const raw = await this.request<any>('/condo/violations/', {
      method: 'POST',
      body: JSON.stringify(violation),
    });
    return this.parseViolation(raw);
  }

  async updateViolation(id: string, violation: Partial<Violation>): Promise<Violation> {
    const raw = await this.request<any>(`/condo/violations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(violation),
    });
    return this.parseViolation(raw);
  }

  async deleteViolation(id: string): Promise<void> {
    return this.request<void>(`/condo/violations/${id}`, {
      method: 'DELETE',
    });
  }

  async sendViolationNotice(id: string): Promise<Violation> {
    const raw = await this.request<any>(`/condo/violations/${id}/notice`, {
      method: 'POST',
    });
    return this.parseViolation(raw);
  }

  async resolveViolation(id: string): Promise<Violation> {
    const raw = await this.request<any>(`/condo/violations/${id}/resolve`, {
      method: 'POST',
    });
    return this.parseViolation(raw);
  }

  async addViolationNote(id: string, note: string): Promise<Violation> {
    const raw = await this.request<any>(`/condo/violations/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ text: note }),
    });
    return this.parseViolation(raw);
  }

  // ============ MAINTENANCE REQUESTS ============

  /** Normalize backend enum strings to lowercase for frontend compatibility */
  private parseMR(r: any): MaintenanceRequest {
    return {
      ...r,
      status:   (r.status   ?? 'submitted').toLowerCase() as MaintenanceRequest['status'],
      priority: (r.priority ?? 'medium').toLowerCase()   as MaintenanceRequest['priority'],
      notes:    r.notes ?? [],
    };
  }

  async getMaintenanceRequests(condoId?: string): Promise<MaintenanceRequest[]> {
    // Sprint 89 follow-up — the manager/admin maintenance screen used to fire
    // GET /condo/maintenance-requests (no condoId) when currentCondo had not
    // yet resolved, which 404s: the backend only exposes the tenant-scoped
    // GET /condo/maintenance-requests/condo/{condoId} (no paramless base GET).
    // Resolve the active condo from persisted auth state before calling, and
    // short-circuit to an empty list if none can be found — same defensive
    // shape as getPayments (R8) / getViolations (R9). The caller re-fires once
    // currentCondo is set, so no real data path is lost.
    const resolvedCondoId = condoId || this.resolveActiveCondoId();
    if (!resolvedCondoId) return [];
    const raw = await this.request<any[]>(`/condo/maintenance-requests/condo/${resolvedCondoId}`);
    return raw.map(r => this.parseMR(r));
  }

  async getMaintenanceRequest(id: string): Promise<MaintenanceRequest> {
    const raw = await this.request<any>(`/condo/maintenance-requests/${id}`);
    return this.parseMR(raw);
  }

  async createMaintenanceRequest(req: Omit<MaintenanceRequest, 'id'>): Promise<MaintenanceRequest> {
    const raw = await this.request<any>('/condo/maintenance-requests/', {
      method: 'POST',
      body: JSON.stringify(req),
    });
    return this.parseMR(raw);
  }

  async updateMaintenanceRequest(id: string, req: Partial<MaintenanceRequest>): Promise<MaintenanceRequest> {
    const raw = await this.request<any>(`/condo/maintenance-requests/${id}`, {
      method: 'PUT',
      body: JSON.stringify(req),
    });
    return this.parseMR(raw);
  }

  async deleteMaintenanceRequest(id: string): Promise<void> {
    return this.request<void>(`/condo/maintenance-requests/${id}`, {
      method: 'DELETE',
    });
  }

  async updateMaintenanceRequestStatus(id: string, status: string): Promise<MaintenanceRequest> {
    const raw = await this.request<any>(`/condo/maintenance-requests/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    return this.parseMR(raw);
  }

  // ============ AMENITIES ============

  async getAmenities(condoId?: string): Promise<Amenity[]> {
    const url = condoId ? `/condo/amenities/condo/${condoId}` : '/condo/amenities';
    return this.request<Amenity[]>(url);
  }

  async getAmenity(id: string): Promise<Amenity> {
    return this.request<Amenity>(`/condo/amenities/${id}`);
  }

  async createAmenity(amenity: Omit<Amenity, 'id'>): Promise<Amenity> {
    return this.request<Amenity>('/condo/amenities/', {
      method: 'POST',
      body: JSON.stringify(amenity),
    });
  }

  async updateAmenity(id: string, amenity: Partial<Amenity>): Promise<Amenity> {
    return this.request<Amenity>(`/condo/amenities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(amenity),
    });
  }

  async deleteAmenity(id: string): Promise<void> {
    return this.request<void>(`/condo/amenities/${id}`, {
      method: 'DELETE',
    });
  }

  // ============ AMENITY BOOKINGS ============

  private parseBooking(b: any): AmenityBooking {
    return {
      ...b,
      status: (b.status ?? 'pending').toLowerCase() as AmenityBooking['status'],
    };
  }

  async getAmenityBookings(condoId?: string): Promise<AmenityBooking[]> {
    const url = condoId ? `/condo/amenity-bookings/condo/${condoId}` : '/condo/amenity-bookings';
    const raw = await this.request<any[]>(url);
    return raw.map(b => this.parseBooking(b));
  }

  async getAmenityBooking(id: string): Promise<AmenityBooking> {
    const raw = await this.request<any>(`/condo/amenity-bookings/${id}`);
    return this.parseBooking(raw);
  }

  async createAmenityBooking(booking: Omit<AmenityBooking, 'id'>): Promise<AmenityBooking> {
    const raw = await this.request<any>('/condo/amenity-bookings/', {
      method: 'POST',
      body: JSON.stringify(booking),
    });
    return this.parseBooking(raw);
  }

  async updateAmenityBooking(id: string, booking: Partial<AmenityBooking>): Promise<AmenityBooking> {
    const raw = await this.request<any>(`/condo/amenity-bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(booking),
    });
    return this.parseBooking(raw);
  }

  async deleteAmenityBooking(id: string): Promise<void> {
    return this.request<void>(`/condo/amenity-bookings/${id}`, {
      method: 'DELETE',
    });
  }

  async approveAmenityBooking(id: string): Promise<AmenityBooking> {
    const raw = await this.request<any>(`/condo/amenity-bookings/${id}/approve`, {
      method: 'POST',
    });
    return this.parseBooking(raw);
  }

  async cancelAmenityBooking(id: string): Promise<AmenityBooking> {
    const raw = await this.request<any>(`/condo/amenity-bookings/${id}/cancel`, {
      method: 'POST',
    });
    return this.parseBooking(raw);
  }

  // ============ END NEW ENDPOINTS ============

  // ============ INSPECTION TRACKING ============

  private parseInspection(i: any): Inspection {
    return {
      ...i,
      status:   (i.status ?? 'scheduled').toLowerCase() as Inspection['status'],
      findings: i.findings ?? [],
      notes:    i.notes    ?? [],
    };
  }

  async getInspections(condoId?: string): Promise<Inspection[]> {
    const url = condoId ? `/condo/inspections/condo/${condoId}` : '/condo/inspections';
    const raw = await this.request<any[]>(url);
    return raw.map(i => this.parseInspection(i));
  }

  async createInspection(inspection: Omit<Inspection, 'id'>): Promise<Inspection> {
    const raw = await this.request<any>('/condo/inspections/', {
      method: 'POST',
      body: JSON.stringify(inspection),
    });
    return this.parseInspection(raw);
  }

  async updateInspection(id: string, inspection: Partial<Inspection>): Promise<Inspection> {
    const raw = await this.request<any>(`/condo/inspections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(inspection),
    });
    return this.parseInspection(raw);
  }

  async deleteInspection(id: string): Promise<void> {
    return this.request<void>(`/condo/inspections/${id}`, { method: 'DELETE' });
  }

  async completeInspection(id: string, result: string): Promise<Inspection> {
    const raw = await this.request<any>(`/condo/inspections/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ result }),
    });
    return this.parseInspection(raw);
  }

  async addInspectionNote(id: string, note: string): Promise<Inspection> {
    const raw = await this.request<any>(`/condo/inspections/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ text: note }),
    });
    return this.parseInspection(raw);
  }

  // ============ PACKAGE TRACKING ============

  private parsePkg(p: any): PackageItem {
    return {
      ...p,
      status: (p.status ?? 'received').toLowerCase().replace('_', '_') as PackageItem['status'],
    };
  }

  async getPackages(condoId?: string): Promise<PackageItem[]> {
    const url = condoId ? `/condo/packages/condo/${condoId}` : '/condo/packages';
    const raw = await this.request<any[]>(url);
    return raw.map(p => this.parsePkg(p));
  }

  async createPackage(pkg: Omit<PackageItem, 'id'>): Promise<PackageItem> {
    const raw = await this.request<any>('/condo/packages/', {
      method: 'POST',
      body: JSON.stringify(pkg),
    });
    return this.parsePkg(raw);
  }

  async updatePackage(id: string, pkg: Partial<PackageItem>): Promise<PackageItem> {
    const raw = await this.request<any>(`/condo/packages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(pkg),
    });
    return this.parsePkg(raw);
  }

  async deletePackage(id: string): Promise<void> {
    return this.request<void>(`/condo/packages/${id}`, { method: 'DELETE' });
  }

  async notifyPackageResident(id: string): Promise<PackageItem> {
    const raw = await this.request<any>(`/condo/packages/${id}/notify`, { method: 'POST' });
    return this.parsePkg(raw);
  }

  async markPackagePickedUp(id: string): Promise<PackageItem> {
    const raw = await this.request<any>(`/condo/packages/${id}/pickup`, { method: 'POST' });
    return this.parsePkg(raw);
  }
}
